import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Trophy, Music, Palette, Sparkles } from "lucide-react";
import { Navigation } from "../components/Navigation";
import { Logo } from "../components/Logo";

const PILLARS = [
  { icon: Trophy, title: "Sports", text: "From the roar of the stadium to the quiet after the whistle." },
  { icon: Music, title: "Entertainment", text: "Live music, film and the culture of performance." },
  { icon: Palette, title: "Culture", text: "Art, photography and the ideas shaping how we see." },
  { icon: Sparkles, title: "Storytelling", text: "Interactive, image-first journalism built for the screen." },
];

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-ink grain">
      <Navigation />

      {/* Hero */}
      <div className="relative h-[70vh] w-full overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1585314062340-f1a5a7c9328d?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400"
          alt="Phusic"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="absolute inset-0 flex flex-col items-start justify-end px-6 lg:px-10 pb-16">
          <div className="mx-auto max-w-5xl w-full">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              <span className="font-logo text-5xl sm:text-6xl lg:text-7xl text-white" style={{ textShadow: "0 2px 30px rgba(0,0,0,0.8)" }}>
                Phusic
              </span>
              <h1 className="mt-4 font-serif text-2xl sm:text-4xl lg:text-5xl text-white max-w-2xl leading-tight">
                A digital front page for sports, entertainment & culture.
              </h1>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Manifesto */}
      <section className="px-6 lg:px-10 py-14 sm:py-20 border-b border-white/10">
        <div className="mx-auto max-w-3xl">
          <p className="font-body text-xs uppercase tracking-[0.28em] text-crimson mb-6">Our Story</p>
          <div className="space-y-7 font-body text-base sm:text-lg leading-relaxed text-white/75">
            <p>
              Phusic was founded on a simple belief: images move to the sound of life.
            </p>
            <p>
              Like music, a powerful photograph has rhythm. It has emotion, movement, silence, and
              impact. It can make you feel the roar of a stadium, the soul of a performance, the
              energy of a city, and the heartbeat of culture — all in one frame.
            </p>
            <p>
              Founded by <span className="text-white">Ricardo Stallworth</span>, a professional
              photographer, newspaper creator, and visual storyteller with over 40 years of
              experience capturing moments across the world, Phusic is built from a lifetime behind
              the lens. From major music events and legendary sports moments to cultural scenes that
              shaped generations, Ricardo has documented the images that live beyond the headline.
            </p>
            <p>Phusic brings that vision into the digital age.</p>
            <p>
              We created a newspaper where the image comes first — a cinematic front page that
              moves, breathes, and pulls you into the story. We cover the worlds that make culture
              pulse: sports that unite cities, performances that define generations, and art that
              teaches us how to see differently.
            </p>
            <p>
              Every story is told through bold photography, elegant design, and a deep respect for
              the craft.
            </p>
            <p>
              This is journalism designed for the screen — interactive, visual, and alive. A
              newspaper you scroll through like a gallery, experience like music, and remember like
              a moment you were there to witness.
            </p>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="px-6 lg:px-10 py-14 sm:py-20">
        <div className="mx-auto max-w-7xl">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {PILLARS.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                data-testid={`about-pillar-${p.title.toLowerCase()}`}
                className="rounded-lg border border-white/10 bg-surface p-8 hover:border-crimson/60 transition-colors duration-300"
              >
                <p.icon className="text-crimson mb-5" size={28} />
                <h3 className="font-serif text-2xl text-white mb-3">{p.title}</h3>
                <p className="font-body text-sm text-white/60 leading-relaxed">{p.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 lg:px-10 pb-16 sm:pb-24">
        <div className="mx-auto max-w-7xl rounded-2xl border border-white/10 bg-gradient-to-br from-surface to-black p-8 sm:p-12 lg:p-16 text-center">
          <h2 className="font-serif text-2xl sm:text-4xl text-white mb-4">Start on the front page.</h2>
          <p className="font-body text-white/60 mb-8 max-w-lg mx-auto">
            Dive into the featured stories moving through Phusic right now.
          </p>
          <Link
            to="/"
            data-testid="about-cta-home"
            className="inline-flex items-center gap-2 rounded-full bg-white text-black font-body text-sm font-medium uppercase tracking-[0.15em] px-7 py-3.5 hover:bg-crimson hover:text-white transition-colors duration-300"
          >
            Explore Phusic
          </Link>
        </div>
      </section>

      <footer className="border-t border-white/10 px-6 lg:px-10 py-10 flex items-center justify-between">
        <Logo size="text-3xl" />
        <span className="font-body text-xs text-white/40 uppercase tracking-[0.2em]">© 2026 Phusic</span>
      </footer>
    </div>
  );
}
